/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-26
 */

// Add third-party dependencies.
import {
  BottomNavigation,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

// Add local dependencies.
import TabItem from './TabItem';
import { useSessionAuth } from '../../../../hooks/useSessionAuth';

/**
 * Tab configuration interface.
 */
interface TabConfig {
  id: string;
  label: string;
  icon: React.ComponentType;
  path: string;
  badge?: number;
  requiresAuth: boolean;
}

/**
 * Props for the BottomTabBar component.
 */
interface BottomTabBarProps {
  /**
   * Additional CSS classes.
   */
  className?: string;
  /**
   * Notification counts for badges.
   */
  notificationCounts?: {
    dashboard?: number;
    studios?: number;
    tools?: number;
    profile?: number;
  };
}

/**
 * Bottom tab bar component for mobile navigation.
 * Provides fixed bottom navigation with primary app sections.
 * Only visible on mobile devices for authenticated users.
 *
 * @param props - The component props
 * @returns {React.ReactElement | null} The bottom tab bar component or null if not applicable
 *
 * @example
 * return (
 *   <BottomTabBar
 *     notificationCounts={{
 *       dashboard: 3,
 *       studios: 1,
 *     }}
 *   />
 * );
 */
const BottomTabBar: React.FC<BottomTabBarProps> = ({
  className,
  notificationCounts = {},
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSessionAuth();

  // Only show on mobile devices (< 768px)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Define tab configuration
  const tabs: TabConfig[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: DashboardIcon,
      path: '/dashboard',
      badge: notificationCounts.dashboard,
      requiresAuth: true,
    },
    {
      id: 'studios',
      label: 'Studios',
      icon: BusinessIcon,
      path: '/studios',
      badge: notificationCounts.studios,
      requiresAuth: true,
    },
    {
      id: 'tools',
      label: 'Tools',
      icon: BuildIcon,
      path: '/tools',
      badge: notificationCounts.tools,
      requiresAuth: true,
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: PersonIcon,
      path: '/profile',
      badge: notificationCounts.profile,
      requiresAuth: true,
    },
  ];

  // Don't render if not mobile or not authenticated
  if (!isMobile || !isAuthenticated) {
    return null;
  }

  // Determine current active tab based on location
  const getCurrentTabIndex = (): number => {
    const currentPath = location.pathname;

    // Handle studio-specific paths
    if (
      currentPath.startsWith('/studio') &&
      !currentPath.startsWith('/studios')
    ) {
      return tabs.findIndex(tab => tab.id === 'studios');
    }

    // Handle exact matches and path prefixes
    const activeTabIndex = tabs.findIndex(tab => {
      if (tab.path === currentPath) return true;
      if (tab.id === 'tools' && currentPath.startsWith('/tools')) return true;
      return false;
    });

    return activeTabIndex >= 0 ? activeTabIndex : -1;
  };

  /**
   * Handle tab change.
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = tabs[newValue];
    if (selectedTab) {
      navigate(selectedTab.path);
    }
  };

  return (
    <Paper
      className={className}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        // Handle safe area insets for devices with home indicators
        paddingBottom: 'env(safe-area-inset-bottom)',
        borderTop: `1px solid ${theme.palette.divider}`,
        // Ensure it's above other content
        boxShadow: theme.shadows[8],
      }}
      elevation={0}
    >
      <BottomNavigation
        value={getCurrentTabIndex()}
        onChange={handleTabChange}
        showLabels
        sx={{
          height: 64,
          backgroundColor: theme.palette.background.paper,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 12px 8px',
            // Ensure touch targets are at least 44px
            minHeight: 44,
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.75rem',
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: '0.75rem',
            },
          },
        }}
      >
        {tabs.map((tab, index) => (
          <TabItem
            key={tab.id}
            label={tab.label}
            icon={tab.icon}
            badge={tab.badge}
            value={index}
            onClick={() => navigate(tab.path)}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default BottomTabBar;
